import { createI18n, type DefaultNumberFormatSchema } from 'vue-i18n';
import type { App } from 'vue';
import extractGlobImports from '@/utils/extractGlobImports';

const STORE_LANG_KEY = 'i18n-lang';

export const getInitLocale = () => localStorage.getItem(STORE_LANG_KEY) || 'it';
export const setInitLocal = (locale: string) =>
  localStorage.setItem(STORE_LANG_KEY, locale);

const importLocales = extractGlobImports(
  import.meta.glob('/src/i18n/locales/**/*.json', {
    import: 'default',
  }),
  /\/i18n\/locales\/([^/]+)\/index\.json/,
);

const formats: DefaultNumberFormatSchema = {
  currency: {
    style: 'currency',
    currency: 'EUR',
  },
  decimal: {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
  percent: {
    style: 'percent',
    minimumFractionDigits: 2,
  },
  emission: {
    style: 'decimal',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  },
  round: {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
};

const defaultLocale = getInitLocale();

const instanceI18n = createI18n({
  legacy: false,
  locale: defaultLocale, // set locale
  fallbackLocale: 'en',
  formatFallbackMessages: true,
  missingWarn: false,
  fallbackWarn: false,
  globalInjection: true,
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  numberFormats: {
    en: formats,
    it: formats,
    ro: formats,
    es: formats,
    zh: formats,
  },
});

const createPluginI18n = (app: App) => {
  app.use(instanceI18n);
};

export const i18n = instanceI18n.global;
export const loadLocaleMessages = async (locale = defaultLocale) => {
  const messages = await importLocales[locale]();
  i18n.setLocaleMessage(locale, messages);

  return nextTick();
};

export const COUNTRY_LANGUAGE_MAP = {
  italy: 'it',
  mexico: 'es',
  romania: 'ro',
  'united states': 'en',
  spain: 'es',
};

export const setlanguageByCountry = async (countryName: string) => {
  const countryKey = countryName.toLowerCase();
  const languageCode = COUNTRY_LANGUAGE_MAP[countryKey];
  if (languageCode && i18n.locale.value !== languageCode) {
    setInitLocal(languageCode);
    i18n.locale.value = languageCode;
    await loadLocaleMessages(languageCode);
  }
};

export default createPluginI18n;
